import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { TextField, AreaField, ConsentBox, Honeypot } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, apiError } from "@/lib/api";

function Success({ onReset }) {
  return (
    <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-10 text-center" data-testid="support-success">
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald" />
      <h3 className="mt-4 font-display text-2xl text-navy font-semibold">Thank you — inquiry received</h3>
      <p className="mt-3 text-muted-foreground">
        Submitting an inquiry does not guarantee funding, representation, or placement, but we're
        grateful to learn about your work and will review it prayerfully.
      </p>
      <Button className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8" onClick={onReset}>Submit another</Button>
    </div>
  );
}

const MIN_EMPTY = { contact_name: "", ministry_name: "", email: "", phone: "", mission: "", current_work: "", community: "", growth_needs: "", consent: false, hp_field: "" };
const ART_EMPTY = { contact_name: "", email: "", phone: "", discipline: "", portfolio_url: "", faith_work: "", support_type: "", consent: false, hp_field: "" };

function useSubmit(path) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const send = async (form) => {
    if (!form.consent) { toast.error("Please provide consent to be contacted."); return false; }
    setSubmitting(true);
    try {
      await api.post(path, form);
      setDone(true);
      window.scrollTo({ top: 0 });
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setSubmitting(false); }
  };
  return { submitting, done, setDone, send };
}

function MinistryForm() {
  const [form, setForm] = useState(MIN_EMPTY);
  const { submitting, done, setDone, send } = useSubmit("/inquiries/ministry");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);
  if (done) return <Success onReset={() => { setForm(MIN_EMPTY); setDone(false); }} />;
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(form); }} className="grid gap-5" data-testid="ministry-form">
      <Honeypot value={form.hp_field} onChange={set("hp_field")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Your name" name="contact_name" required value={form.contact_name} onChange={onInput("contact_name")} />
        <TextField label="Ministry name" name="ministry_name" required value={form.ministry_name} onChange={onInput("ministry_name")} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Email" name="email" type="email" required value={form.email} onChange={onInput("email")} />
        <TextField label="Phone (optional)" name="phone" value={form.phone} onChange={onInput("phone")} />
      </div>
      <AreaField label="Your mission" name="mission" required value={form.mission} onChange={onInput("mission")} />
      <AreaField label="Current work" name="current_work" value={form.current_work} onChange={onInput("current_work")} />
      <TextField label="Community you serve" name="community" value={form.community} onChange={onInput("community")} />
      <AreaField label="Growth needs" name="growth_needs" value={form.growth_needs} onChange={onInput("growth_needs")} hint="Where could support help you grow and reach your community?" />
      <ConsentBox checked={form.consent} onChange={set("consent")}>I consent to be contacted by Lord Almighty about this inquiry.</ConsentBox>
      <div><Button type="submit" disabled={submitting} className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-10 py-6" data-testid="ministry-submit">{submitting ? "Sending…" : "Submit Ministry Inquiry"}</Button></div>
    </form>
  );
}

function ArtistForm() {
  const [form, setForm] = useState(ART_EMPTY);
  const { submitting, done, setDone, send } = useSubmit("/inquiries/artist");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);
  if (done) return <Success onReset={() => { setForm(ART_EMPTY); setDone(false); }} />;
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(form); }} className="grid gap-5" data-testid="artist-form">
      <Honeypot value={form.hp_field} onChange={set("hp_field")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Your name" name="contact_name" required value={form.contact_name} onChange={onInput("contact_name")} />
        <TextField label="Discipline" name="discipline" required value={form.discipline} onChange={onInput("discipline")} hint="e.g. musician, painter, writer, filmmaker" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Email" name="email" type="email" required value={form.email} onChange={onInput("email")} />
        <TextField label="Phone (optional)" name="phone" value={form.phone} onChange={onInput("phone")} />
      </div>
      <TextField label="Portfolio or website" name="portfolio_url" value={form.portfolio_url} onChange={onInput("portfolio_url")} placeholder="https://" />
      <AreaField label="Your faith-centered work" name="faith_work" required value={form.faith_work} onChange={onInput("faith_work")} />
      <AreaField label="Type of support you seek" name="support_type" required value={form.support_type} onChange={onInput("support_type")} />
      <ConsentBox checked={form.consent} onChange={set("consent")}>I consent to be contacted by Lord Almighty about this inquiry.</ConsentBox>
      <div><Button type="submit" disabled={submitting} className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-10 py-6" data-testid="artist-submit">{submitting ? "Sending…" : "Submit Artist Inquiry"}</Button></div>
    </form>
  );
}

export default function MinistryArtist() {
  usePageMeta("Ministry & Artist Support",
    "Support for growing Christian ministries and artists. Introduce your work and the support you seek.");
  return (
    <div>
      <PageHero
        eyebrow="Ministry & Artist Support"
        title="Encouraging those who serve and those who create"
        subtitle="We love to come alongside growing ministries and Christian artists. Introduce your work below. Submitting an inquiry does not guarantee funding, representation, or placement — but it opens a conversation."
        image="https://images.unsplash.com/photo-1614107070261-2aa1304618f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjaHJpc3RpYW4lMjBhcnRpc3QlMjBhdCUyMHdvcmt8ZW58MHx8fHwxNzkwNjA2MzcyfDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="mx-auto max-w-3xl px-5 lg:px-8 py-20">
        <Reveal>
          <Tabs defaultValue="ministry">
            <TabsList className="grid w-full grid-cols-2 bg-ivory-alt">
              <TabsTrigger value="ministry" data-testid="tab-ministry" className="data-[state=active]:bg-emerald data-[state=active]:text-white">For Ministries</TabsTrigger>
              <TabsTrigger value="artist" data-testid="tab-artist" className="data-[state=active]:bg-emerald data-[state=active]:text-white">For Artists</TabsTrigger>
            </TabsList>
            <TabsContent value="ministry" className="mt-8"><MinistryForm /></TabsContent>
            <TabsContent value="artist" className="mt-8"><ArtistForm /></TabsContent>
          </Tabs>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-12 text-sm text-muted-foreground border-t border-border pt-6">
            Ministry and artist spotlights will appear on this site as they are prepared and approved.
            We do not publish examples as real recipients unless they truly are.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
